import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { message } from 'antd';
import { capitalize } from 'lodash';
import moment from 'moment';
import IntracityTripForm from 'components/forms/IntracityTripForm';
import { getClientList, getLocations } from 'store/clientSlice';
import { addUser, getPincodeList, getTripListActionV2 } from 'store/pageSlice';
import { getSignedUrl } from 'store/commonSlice';
import { search_type, getuniqueArray } from 'utils';
import { useAuth } from 'hooks';
import { getServerFormatedDate } from 'utils/helpers';
import { getUIVersion } from 'utils/router-static-data';
import { searchDriversV2 } from 'store/driverSliceV2';
import { getProjectListV2 } from 'store/projectSliceV2';
import { getVendorContractListV2, searchVendorV2 } from 'store/vendorSliceV2';
import { getRouteListV2 } from 'store/routeSliceV2';
import { searchVehicleTypesV2, searchVehiclesV2 } from 'store/vehicleSliceV2';
import { reuploadPoR } from 'store/approvalSlice';

export default function CreateIntracityTrip() {
	const { permissions } = useAuth();
	const [formData, setFormData] = useState({});
	const { cities = [] } = useSelector((state) => state.clientState);
	const dispatch = useDispatch();
	const { id } = useParams();
	const location = useLocation();
	// Editing a low-margin submission that is still waiting for City Manager approval.
	// No trip exists yet — the whole submission lives on the adhoc request, so this form
	// loads from that stash and saves back to it rather than creating anything.
	const marginRequestId = location.state?.marginRequestId;
	const marginRequestPayload = location.state?.marginRequestPayload;
	const isMarginRequestEdit = !!marginRequestId && !!marginRequestPayload;
	const isEdit = !!id;
	const [projectData, setProjects] = useState([]);
	const [cityOptionsV2, setCityOptionsV2] = useState([]);
	const [vendorsData, setVendordata] = useState([]);
	const [VehicleTypes, setVehicleTypes] = useState([]);
	const [vehicleTypeIdMap, setVehicleTypeIdMap] = useState({}); // Map vehicle_type string to vehicle_type_id
	const [routeData, setRouteData] = useState([]); // Routes for v2 API
	const [routeDetailsById, setRouteDetailsById] = useState({});
	const [vehicleData, setVehicleData] = useState([]);
	const [driverData, setDriverData] = useState([]);
	const [ProjectAdhocFlag, SetProjectAdhocFlag] = useState(false);
	const [crossVendorAllowed, setCrossVendorAllowed] = useState(false);
	const [sourceCity, setSourceCity] = useState('');
	const [destCity, setDestCity] = useState('');
	// Separate upload states to avoid cross-loading between different uploaders
	const [uploadingOdometer, setUploadingOdometer] = useState(false);
	const [uploadErrorOdometer, setUploadErrorOdometer] = useState('');
	const [uploadingProof, setUploadingProof] = useState(false);
	const [uploadErrorProof, setUploadErrorProof] = useState('');
	const [loading, setLoading] = useState(false);
	const formRef = useRef();
	// PoR doc key as loaded (before edits) — used to detect a replacement on save.
	const originalPorKeyRef = useRef(null);
	const hasFetchedLocationsRef = useRef(false);
	const editBootstrapKeyRef = useRef('');
	const navigate = useNavigate();
	const [isV2UI, setIsV2UI] = useState(() => getUIVersion() === 'new');

	const getIntermediaryAddresses = (tripData) => {
		if (Array.isArray(tripData?.intermediary_addresses)) {
			return tripData.intermediary_addresses.filter(Boolean);
		}

		if (Array.isArray(tripData?.address?.intermediary)) {
			return tripData.address.intermediary.filter(Boolean);
		}

		return [];
	};

	const getNodeSequence = (tripData, intermediaryAddresses) => {
		if (Array.isArray(tripData?.node_sequence) && tripData.node_sequence.length > 0) {
			return tripData.node_sequence;
		}

		const startAddress = tripData?.address?.source || tripData?.start_address || '';
		const destinationAddress =
			tripData?.address?.destination || tripData?.destination_address || '';
		const nodes = [];

		if (startAddress) {
			nodes.push({ address: startAddress, type: 'start' });
		}

		intermediaryAddresses.forEach((address) => {
			nodes.push({ address, type: 'intermediary' });
		});

		if (destinationAddress) {
			nodes.push({ address: destinationAddress, type: 'end' });
		}

		return nodes;
	};

	// Reused messages
	const FILE_SIZE_LIMIT_MSG = 'File size must be less than 5 MB';
	const UPLOAD_FAILED_MSG = 'Upload failed';
	const UPLOAD_SUCCESS_MSG = 'File uploaded!';
	const INTRACITY_TRIPS_ROUTE = '/trips/intracity';

	const vendorUserType = [
		{ value: 'regular', label: 'Regular' },
		{ value: 'temporary', label: 'Temporary' },
	];

	// Check UI version on mount and when it changes
	useEffect(() => {
		setIsV2UI(getUIVersion() === 'new');
		const handleStorageChange = () => {
			setIsV2UI(getUIVersion() === 'new');
		};
		window.addEventListener('storage', handleStorageChange);
		window.addEventListener('uiVersionChanged', handleStorageChange);
		return () => {
			window.removeEventListener('storage', handleStorageChange);
			window.removeEventListener('uiVersionChanged', handleStorageChange);
		};
	}, []);

	// For V2 adhoc trips
	useEffect(() => {
		if (!isV2UI) return;
		dispatch(
			searchVendorV2({
				filter_by: { is_active: true },
				pagination: { page: 1, per_page: 1000 },
			})
		).then((res) => {
			const data_vendor = (res?.rows || []).map((item) => ({
				value: item?.id,
				label: item?.vendor_name || item?.name,
			}));
			setVendordata(data_vendor);
		});
	}, [dispatch, isV2UI]);

	useEffect(() => {
		if (!isV2UI) return;
		dispatch(
			searchVehicleTypesV2({
				filter_by: { is_active: true },
				pagination: { page: 1, per_page: 1000 },
			})
		).then((resVehical) => {
			const rows = resVehical?.rows || [];
			const data_vechicleType = rows.map((item) => ({
				value: item?.vehicle_type_name,
				label: item?.vehicle_type_name,
			}));

			const typeIdMap = {};
			rows.forEach((item) => {
				const typeName = item?.vehicle_type_name;
				const typeId = item?.id;
				if (typeName && typeId) {
					typeIdMap[typeName] = typeId;
				}
			});

			setVehicleTypes(getuniqueArray(data_vechicleType));
			setVehicleTypeIdMap((prev) => ({ ...prev, ...typeIdMap }));
		});
	}, [dispatch, isV2UI]);

	const canSearchLocation =
		permissions?.search?.includes('location') || permissions?.search?.includes('all');

	useEffect(() => {
		if (hasFetchedLocationsRef.current) return;

		if (isV2UI) {
			dispatch(
				getProjectListV2({
					filter_by: { is_active: true },
					pagination: { page: 1, per_page: 1000 },
				})
			).then((res) => {
				const uniqueCities = [
					...new Set((res?.rows || []).map((item) => item?.city).filter(Boolean)),
				];
				setCityOptionsV2(
					uniqueCities.map((city) => ({
						value: city,
						label: city,
					}))
				);
			});
			hasFetchedLocationsRef.current = true;
			return;
		}

		if (canSearchLocation) {
			dispatch(
				getLocations({
					search_type: search_type?.location,
					filter_by: {},
				})
			);
			hasFetchedLocationsRef.current = true;
		}
	}, [dispatch, canSearchLocation, isV2UI]);

	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (id) {
			const bootstrapKey = `${id}-${isV2UI ? 'v2' : 'v1'}`;
			if (editBootstrapKeyRef.current === bootstrapKey) {
				return;
			}
			editBootstrapKeyRef.current = bootstrapKey;

			setLoading(true);
			const tripFetchAction = isV2UI
				? getTripListActionV2({
						search_type: 'trip',
						id,
						limit: 1,
						offset: 0,
						fetch_filters: false,
				  })
				: getClientList({
						search_type: 'trip',
						filter_by: { id },
				  });

			dispatch(tripFetchAction)
				.then((res) => {
					const trip = isV2UI
						? res?.rows?.[0] ||
						  res?.data?.trips?.[0] ||
						  res?.data?.data?.trips?.[0] ||
						  {}
						: res?.data?.[0] || {};
					const info = trip.info || trip.trip_info || {};
					const addressData = trip.address || info.address || {};
					const intermediaryAddresses = getIntermediaryAddresses({
						...trip,
						address: addressData,
						intermediary_addresses:
							trip.intermediary_addresses || info.intermediary_addresses,
					});
					const nodeSequence = getNodeSequence(
						{
							...trip,
							address: addressData,
							start_address: trip.start_address || info.start_address,
							destination_address:
								trip.destination_address || info.destination_address,
							node_sequence: trip.node_sequence || info.node_sequence,
						},
						intermediaryAddresses
					);

					// For V2: backend already returns dates in IST, no offset needed.
					// For V1: convert UTC to IST (UTC+5:30).
					const toMomentForForm = (dateValue) => {
						if (!dateValue) return undefined;
						if (isV2UI) {
							const m = moment(
								dateValue,
								['MM/DD/YYYY HH:mm:ss', moment.ISO_8601],
								true
							);
							if (m.isValid()) return m;
							const fallbackMoment = moment(dateValue);
							return fallbackMoment.isValid() ? fallbackMoment : undefined;
						}
						// V1: UTC → IST
						const date = new Date(dateValue);
						if (Number.isNaN(date.getTime())) return undefined;
						date.setHours(date.getHours() + 5);
						date.setMinutes(date.getMinutes() + 30);
						return moment(date);
					};

					const reportingDateTimeForForm = isV2UI
						? trip.reporting_date || trip.reporting_time
						: trip.reporting_time;
					const startDateTimeForForm =
						trip.start_date || trip.start_time || trip.actual_start_time;
					const endDateTimeForForm = trip.end_time;

					setFormData({
						id: trip.id, // <-- ensure id is present
						trip_type: trip.trip_type || 'adhoc',
						city: trip.city || '',
						project_id: trip.project_id || info.project_id || '',
						vendor_id: trip.vendor_id || info.vendor_id || '',
						vendor_type:
							trip.vendor_type ||
							trip.vendor_details?.vendor_type ||
							info.vendor_type ||
							'',
						project_location_code:
							trip.project_name ||
							trip.project_location_code ||
							info.project_location_code ||
							'',
						vendor_name: trip.vendor_name || info.vendor_name || '',
						vehicle_number: trip.vehicle_number || '',
						reporting_date: toMomentForForm(reportingDateTimeForForm),
						start_date: toMomentForForm(startDateTimeForForm),
						end_date: toMomentForForm(endDateTimeForForm),
						route_type: trip.route_type || info.route_type,
						start_address:
							addressData.source || trip.start_address || info.start_address || '',
						destination_address:
							addressData.destination ||
							trip.destination_address ||
							info.destination_address ||
							'',
						intermediary_addresses: intermediaryAddresses,
						node_sequence: nodeSequence,
						start_location_pincode: trip.source_pincode || '',
						end_location_pincode: trip.destination_pincode || '',
						vehicle_type: trip.vehicle_type || trip.vehicle_type_name || '',
						driver_id: trip.driver_id || info.driver_id || '',
						driver_name: info.driver_name || trip.driver_name || '',
						driver_number:
							info.driver_number || trip.driver_mobile || trip.driver_number || '',
						odometer_start: (() => {
							if (trip.start_odometer_image) return [trip.start_odometer_image];
							if (info.start_odometer_image) return [info.start_odometer_image];
							const docStart =
								trip.trip_documents?.odometer_start ||
								info.trip_documents?.odometer_start;
							if (Array.isArray(docStart) && docStart.length > 0) return docStart;
							if (typeof docStart === 'string') return [docStart];
							return [];
						})(),
						requirement_proof:
							(trip.por_approval_status || info.por_approval_status) === 'rejected'
								? []
								: info.requirement_proof || [],
						por_approval_status:
							trip.por_approval_status || info.por_approval_status || null,
						is_approved: trip.is_approved === true,
						client_rate:
							info.client_rate || trip.client_trip_cost || trip.client_rate || '',
						vendor_rate:
							info.vendor_rate || trip.vendor_trip_cost || trip.vendor_rate || '',
						vendor_rate_basis: trip.vendor_rate_basis || info.vendor_rate_basis || '',
						billing_type: trip.billing_type || info.billing_type || '',
						billing_subtype: trip.billing_subtype || info.billing_subtype || '',
						current_vendor_rate: trip.current_vendor_rate,
						current_charges_per_km: trip.current_charges_per_km,
						order_rates: trip.order_rates || info.order_rates || [],
						start_km: trip.start_km || trip.start_odo || '',
						route_id: trip.route_id || null, // Add route_id for v2
						source_entry: trip.source_entry || '',
						trip_status_id: trip.trip_status_id,
						isEnded:
							trip.trip_status_id === 3 ||
							(typeof trip.trip_status === 'string' &&
								trip.trip_status.toLowerCase() === 'trip_closed'),
						// Cross-vendor vehicle: client eligibility + whether this trip already uses one
						allow_cross_vendor_vehicle: trip.allow_cross_vendor_vehicle || false,
						is_cross_vendor_vehicle: trip.is_cross_vendor_vehicle || false,
						vehicle_vendor_name: trip.vehicle_vendor_name || '',
						vehicle_vendor_id: trip.vehicle_vendor_id || null,
						remarks: trip.remarks || '',
					});

					// Remember the PoR doc that was on the trip before editing (for reupload_por).
					originalPorKeyRef.current = (info.requirement_proof || [])[0] || null;

					// Fetch routes for v2 API when editing
					if (isV2UI && trip.project_id) {
						fetchRoutesByProject(trip.project_id);
					}

					// For v2 edit mode, preload dependent dropdown data for both regular and adhoc
					if (isV2UI && trip.vendor_id && (trip.vehicle_type || trip.vehicle_type_name)) {
						resolveVehicleTypeId(trip.vehicle_type || trip.vehicle_type_name).then(
							async (vehicleTypeId) => {
								if (vehicleTypeId) {
									await getVehiclesData(trip.vendor_id, vehicleTypeId);
									await getDriverDataV2(
										trip.vehicle_vendor_id || trip.vendor_id,
										vehicleTypeId,
										trip.driver_id,
										trip.driver_name
									);
								}
							}
						);
					}

					// Load project data for the city
					if (trip.city) {
						if (isV2UI) {
							dispatch(
								getProjectListV2({
									filter_by: {
										city: trip.city,
										is_active: true,
									},
									pagination: { page: 1, per_page: 1000 },
								})
							).then((projectRes) => {
								const prodata = (projectRes?.rows || []).map((item) => ({
									label: capitalize(item?.project_name),
									value: item?.id,
								}));
								setProjects(prodata);
							});
						} else {
							dispatch(
								getClientList({
									search_type: 'project',
									filter_by: {
										city: trip.city,
										is_active: true,
									},
								})
							).then((projectRes) => {
								const arr = Array.isArray(projectRes?.data)
									? projectRes?.data
									: Object.values(projectRes?.data || {});
								const prodata = arr.map((item) => ({
									label: capitalize(item?.project_location_code),
									value: item?.id,
								}));
								setProjects(prodata);
							});
						}
					}

					// Load vendor data for regular trips
					if (trip.trip_type === 'regular' && trip.project_id) {
						if (isV2UI) {
							dispatch(
								getVendorContractListV2({
									filter_by: {
										project_id: trip.project_id,
										city: trip.city,
										is_active: true,
									},
									pagination: { page: 1, per_page: 1000 },
								})
							).then(async (vendorRes) => {
								const contracts = vendorRes?.rows || [];
								const data_vendor = contracts.map((item) => ({
									value: item?.vendor_id,
									label: item?.vendor_name,
								}));
								setVendordata(getuniqueArray(data_vendor));

								const typeIdMap = {};
								contracts.forEach((item) => {
									const typeName = item?.vehicle_type || item?.vehicle_type_name;
									const typeId = item?.vehicle_type_id;
									if (typeName && typeId) {
										typeIdMap[typeName] = typeId;
									}
								});
								setVehicleTypeIdMap((prev) => ({ ...prev, ...typeIdMap }));

								const vehicleTypeId = await resolveVehicleTypeId(
									trip.vehicle_type || trip.vehicle_type_name
								);
								if (trip.vendor_id && vehicleTypeId) {
									await getVehiclesData(trip.vendor_id, vehicleTypeId);
									await getDriverDataV2(
										trip.vehicle_vendor_id || trip.vendor_id,
										vehicleTypeId,
										trip.driver_id,
										trip.driver_name
									);
								}
							});
						} else {
							dispatch(
								getClientList({
									search_type: 'vendor_contract',
									filter_by: {
										project_id: trip.project_id,
										city: trip.city,
										is_active: true,
									},
								})
							).then((vendorRes) => {
								const arr = Array.isArray(vendorRes?.data)
									? vendorRes?.data
									: Object.values(vendorRes?.data || {});
								const data_vendor = arr.map((item) => ({
									value: item?.vendor_id,
									label: item?.vendor_name,
								}));
								setVendordata(data_vendor);

								// Load driver data for regular trips
								if (trip.vehicle_type && trip.vendor_id) {
									getDriverData(trip.vehicle_type, trip.vendor_id);
								}
							});
						}
					}

					// Load vendor data for adhoc trips
					if (trip.trip_type === 'adhoc') {
						getVendorDataList(trip.vendor_type);
					}

					// Load vehicle types
					getVehicleTypes();

					setLoading(false);
				})
				.catch(() => setLoading(false));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, id, isV2UI]);

	function fetchRoutesByProject(projectId) {
		if (!isV2UI || !projectId) {
			setRouteData([]);
			setRouteDetailsById({});
			return Promise.resolve();
		}

		return dispatch(
			getRouteListV2({
				filter_by: {
					project_id: Number(projectId),
					is_active: true,
				},
				pagination: { page: 1, per_page: 1000 },
				sort_by: { field: 'created_at', order: 'desc' },
			})
		)
			.then((routeRes) => {
				const routes = routeRes?.rows || [];
				const routeOptions = routes.map((route) => ({
					value: route.id,
					label: route.route_name || route.route_code || `Route ${route.id}`,
				}));
				const detailsById = {};
				routes.forEach((route) => {
					if (route?.id) {
						detailsById[route.id] = route;
					}
				});
				setRouteData(routeOptions);
				setRouteDetailsById(detailsById);
			})
			.catch(() => {
				setRouteData([]);
				setRouteDetailsById({});
			});
	}

	function handleCitiesChange(cityValue, setFieldValue) {
		setFieldValue('project_id', undefined);
		if (isV2UI) {
			setFieldValue('route_id', undefined);
			setRouteData([]);
			setRouteDetailsById({});
			setVehicleData([]);
			setDriverData([]);
		}
		if (!cityValue) {
			setProjects([]);
			return;
		}

		if (isV2UI) {
			// Use v2 API for projects
			dispatch(
				getProjectListV2({
					filter_by: {
						city: cityValue,
						is_active: true,
					},
					pagination: { page: 1, per_page: 1000 },
				})
			).then((res) => {
				const prodata = (res?.rows || []).map((item) => ({
					label: capitalize(item?.project_name),
					value: item?.id,
				}));
				setProjects(prodata);
			});
		} else {
			// Use v1 API for projects
			dispatch(
				getClientList({
					search_type: 'project',
					filter_by: {
						city: cityValue,
						is_active: true,
					},
				})
			).then((res) => {
				const arr = Array.isArray(res?.data) ? res?.data : Object.values(res?.data || {});
				const prodata = arr.map((item) => ({
					label: capitalize(item?.project_location_code),
					value: item?.id,
				}));
				setProjects(prodata);
			});
		}
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	function handleProjectChange(e, setFieldValue, values) {
		if (setFieldValue) {
			setFieldValue('project_id', e);
			setFieldValue('vehicle_type', undefined);
			setFieldValue('vehicle_number', undefined);
			setFieldValue('driver_id', undefined);
			setFieldValue('driver_number', '');
			setFieldValue('vendor_id', undefined);
			setFieldValue('vendor_type', undefined);
			if (isV2UI) {
				setFieldValue('route_id', undefined);
			}
		}
		if (isV2UI) {
			setVehicleData([]);
			setDriverData([]);
		}

		// Always refresh route options for selected project in v2 flow
		fetchRoutesByProject(e);

		// For regular trips: fetch vendor contracts and vehicle types
		if (
			values.trip_type === 'regular' &&
			(permissions?.search?.includes('vendor_contract') ||
				permissions?.search?.includes('all'))
		) {
			if (isV2UI) {
				// Use v2 API for vendor contracts
				dispatch(
					getVendorContractListV2({
						filter_by: {
							project_id: Number(e),
							city: values?.city,
							is_active: true,
						},
						pagination: { page: 1, per_page: 1000 },
					})
				).then((res) => {
					const contracts = res?.rows || [];
					const data_vendor = contracts.map((item) => ({
						value: item?.vendor_id,
						label: item?.vendor_name,
					}));
					const data_vechicleType = contracts.map((item) => ({
						value: item?.vehicle_type || item?.vehicle_type_name,
						label: item?.vehicle_type || item?.vehicle_type_name,
					}));
					setVehicleTypes(getuniqueArray(data_vechicleType));
					setVendordata(getuniqueArray(data_vendor));

					// Build vehicle_type_id map for v2 API
					if (isV2UI) {
						const typeIdMap = {};
						contracts.forEach((item) => {
							const typeName = item?.vehicle_type || item?.vehicle_type_name;
							const typeId = item?.vehicle_type_id;
							if (typeName && typeId) {
								typeIdMap[typeName] = typeId;
							}
						});
						setVehicleTypeIdMap(typeIdMap);
					}
				});
			} else {
				// Use v1 API for vendor contracts
				dispatch(
					getClientList({
						search_type: 'vendor_contract',
						filter_by: {
							project_id: e,
							city: values?.city,
							is_active: true,
						},
					})
				).then((res) => {
					const arr = Array.isArray(res?.data)
						? res?.data
						: Object.values(res?.data || {});
					const data_vendor = arr.map((item) => ({
						value: item?.vendor_id,
						label: item?.vendor_name,
					}));
					const data_vechicleType = arr.map((item) => ({
						value: item?.vehicle_type,
						label: item?.vehicle_type,
					}));
					setVehicleTypes(getuniqueArray(data_vechicleType));
					setVendordata(getuniqueArray(data_vendor));

					// Build vehicle_type_id map for v2 API (v1 API might not have vehicle_type_id)
					if (isV2UI) {
						const typeIdMap = {};
						arr.forEach((item) => {
							const typeName = item?.vehicle_type;
							const typeId = item?.vehicle_type_id;
							if (typeName && typeId) {
								typeIdMap[typeName] = typeId;
							}
						});
						setVehicleTypeIdMap((prev) => ({ ...prev, ...typeIdMap }));
					}
				});
			}
		}
		// For adhoc trips: fetch project details and vehicle types, set adhoc flag
		if (
			values.trip_type === 'adhoc' &&
			(permissions?.search?.includes('project') || permissions?.search?.includes('all'))
		) {
			getVehicleTypes();
			getVendorDataList();
			if (isV2UI) {
				// Use v2 API for project details
				dispatch(
					getProjectListV2({
						filter_by: {
							id: Number(e),
							is_active: true,
						},
						pagination: { page: 1, per_page: 1 },
					})
				).then((pro_res) => {
					const projects = pro_res?.rows || [];
					if (projects.length > 0) {
						SetProjectAdhocFlag(projects[0]?.adhoc_approval_required);
						setCrossVendorAllowed(projects[0]?.allow_cross_vendor_vehicle || false);
					}
				});
			} else {
				// Use v1 API for project details
				dispatch(
					getClientList({
						search_type: 'project',
						filter_by: {
							id: e,
							is_active: true,
						},
					})
				).then((pro_res) => {
					const arr = Array.isArray(pro_res?.data)
						? pro_res?.data
						: Object.values(pro_res?.data || {});
					const proFalg = arr.map((item) => ({
						adhoc_approval_required: item?.adhoc_approval_required,
					}));
					if (proFalg && proFalg.length > 0) {
						SetProjectAdhocFlag(proFalg[0]?.adhoc_approval_required);
					}
				});
			}
		}
	}
	async function handleCrossVendorSelect(payload, setFieldValue, values) {
		if (!payload?.vehicle_number) return;
		// Cross-vendor vehicle can be of any type — switch the trip's vehicle type to the chosen
		// vehicle's type and remember its id so driver loading + submit resolve correctly.
		if (payload.vehicle_type_name) {
			setFieldValue('vehicle_type', payload.vehicle_type_name);
			if (payload.vehicle_type_id) {
				setVehicleTypeIdMap((prev) => ({
					...prev,
					[payload.vehicle_type_name]: payload.vehicle_type_id,
				}));
			}
		}
		// Inject the chosen vehicle into the dropdown options so it displays.
		setVehicleData((prev) =>
			prev.find((o) => o.value === payload.vehicle_number)
				? prev
				: [
						{
							value: payload.vehicle_number,
							label: payload.vehicle_number,
							item: {
								id: payload.vehicle_id,
								vehicle_number: payload.vehicle_number,
								vendor_id: payload.vendor_id,
								vehicle_type_id: payload.vehicle_type_id,
							},
						},
						...prev,
				  ]
		);
		setFieldValue('vehicle_number', payload.vehicle_number);
		setFieldValue('cross_vendor_remark', payload.remark || ''); // optional; stored in trip_info
		setFieldValue('driver_id', undefined);
		setFieldValue('driver_name', '');
		setFieldValue('driver_number', '');
		// Load drivers by the chosen vehicle's vendor + its vehicle type.
		const typeId =
			payload.vehicle_type_id ||
			(await resolveVehicleTypeId(payload.vehicle_type_name || values?.vehicle_type));
		if (payload.vendor_id && typeId) {
			await getDriverDataV2(payload.vendor_id, typeId);
		}
	}

	function getVehicleTypes() {
		if (isV2UI) {
			dispatch(
				searchVehicleTypesV2({
					filter_by: { is_active: true },
					pagination: { page: 1, per_page: 1000 },
				})
			).then((resVehical) => {
				const rows = resVehical?.rows || [];
				const data_vechicleType = rows.map((item) => ({
					value: item?.vehicle_type || item?.vehicle_type_name,
					label: item?.vehicle_type || item?.vehicle_type_name,
				}));

				const typeIdMap = {};
				rows.forEach((item) => {
					const typeName = item?.vehicle_type || item?.vehicle_type_name;
					const typeId = item?.id || item?.vehicle_type_id;
					if (typeName && typeId) {
						typeIdMap[typeName] = typeId;
					}
				});

				setVehicleTypes(getuniqueArray(data_vechicleType));
				setVehicleTypeIdMap((prev) => ({ ...prev, ...typeIdMap }));
			});
			return;
		}

		dispatch(
			getClientList({
				search_type: 'vehicle',
				filter_by: {},
			})
		).then((resVehical) => {
			const arr = Array.isArray(resVehical?.data)
				? resVehical?.data
				: Object.values(resVehical?.data || {});
			const data_vechicleType = arr.map((item) => ({
				value: item?.vehicle_type,
				label: item?.vehicle_type,
			}));
			setVehicleTypes(getuniqueArray(data_vechicleType));
		});
	}

	async function resolveVehicleTypeId(vehicleTypeName) {
		if (!vehicleTypeName) return null;

		const mappedTypeId = vehicleTypeIdMap[vehicleTypeName];
		if (mappedTypeId) {
			return Number(mappedTypeId);
		}

		if (!isV2UI) return null;

		try {
			const res = await dispatch(
				searchVehicleTypesV2({
					filter_by: { is_active: true },
					pagination: { page: 1, per_page: 1000 },
				})
			);
			const rows = res?.rows || [];
			const matchedType = rows.find((item) => {
				const typeName = item?.vehicle_type || item?.vehicle_type_name;
				return (
					String(typeName || '').toLowerCase() === String(vehicleTypeName).toLowerCase()
				);
			});

			if (matchedType) {
				const resolvedTypeName =
					matchedType?.vehicle_type || matchedType?.vehicle_type_name;
				const resolvedTypeId = matchedType?.id || matchedType?.vehicle_type_id;
				if (resolvedTypeName && resolvedTypeId) {
					setVehicleTypeIdMap((prev) => ({
						...prev,
						[resolvedTypeName]: resolvedTypeId,
					}));
					return Number(resolvedTypeId);
				}
			}
		} catch (err) {
			console.error('Failed to resolve vehicle_type_id:', err);
		}

		return null;
	}

	function getVendorDataList(type) {
		if (isV2UI) {
			// Use v2 API for vendors
			dispatch(
				searchVendorV2({
					filter_by: {
						is_active: true,
						...(type ? { vendor_type: type } : {}),
					},
					pagination: { page: 1, per_page: 1000 },
				})
			).then((res) => {
				const data_vendor = (res?.rows || []).map((item) => ({
					value: item?.id,
					label: item?.vendor_name || item?.name,
				}));
				setVendordata(data_vendor);
			});
		} else {
			// Use v1 API for vendors
			dispatch(
				getClientList({
					search_type: 'vendor',
					filter_by: {
						is_active: true,
						...(type ? { vendor_type: type } : {}),
					},
				})
			).then((res) => {
				const arr = Array.isArray(res?.data) ? res?.data : Object.values(res?.data || {});
				const data_vendor = arr.map((item) => ({
					value: item?.id,
					label: item?.vendor_info?.vendor_name,
				}));
				setVendordata(data_vendor);
			});
		}
	}

	async function getDriverData(vehicleType, vendorId) {
		if (!(permissions?.search?.includes('driver') || permissions?.search?.includes('all'))) {
			return;
		}

		if (isV2UI) {
			// Use v2 API for drivers — filter by vendor_id and vehicle_type_ids (array)
			const vehicleTypeId = await resolveVehicleTypeId(vehicleType);
			if (!vendorId || !vehicleTypeId) {
				setDriverData([]);
				return;
			}
			dispatch(
				searchDriversV2({
					filter_by: {
						vendor_id: vendorId,
						vehicle_type_ids: [Number(vehicleTypeId)],
						is_active: true,
					},
					offset: 0,
					limit: 1000,
				})
			).then((res) => {
				const data = (res?.rows || []).map((item) => ({
					value: item?.id,
					label: `${item?.driver_name || item?.name || ''} -${
						item?.mobile_number || item?.username || ''
					}`,
					item,
				}));
				setDriverData(data);
			});
		} else {
			// Use v1 API for drivers
			dispatch(
				getClientList({
					search_type: 'driver',
					filter_by: {
						vehicle_type: vehicleType,
						vendor_id: vendorId,
					},
				})
			).then((res) => {
				const data = res?.data?.map((item) => ({
					value: item?.id,
					label: `${item?.driver_name} -${item?.username}`,
					item,
				}));
				setDriverData(data);
			});
		}
	}

	async function getVehiclesData(vendorId, vehicleTypeId, routeId) {
		if (!isV2UI || !vendorId || !vehicleTypeId) {
			setVehicleData([]);
			return;
		}

		try {
			const res = await dispatch(
				searchVehiclesV2({
					filter_by: {
						vendor_id: vendorId,
						vehicle_type_id: Number(vehicleTypeId),
						is_active: true,
						...(routeId ? { route_id: Number(routeId) } : {}),
					},
					pagination: { page: 1, per_page: 1000 },
					sort_by: { field: 'created_at', order: 'desc' },
				})
			);

			const vehicles = res?.rows || [];
			const vehicleOptions = vehicles.map((item) => ({
				value: item?.vehicle_number,
				label: item?.vehicle_number || '-',
				item,
			}));
			setVehicleData(vehicleOptions);
		} catch (err) {
			setVehicleData([]);
		}
	}

	async function getDriverDataV2(
		vendorId,
		vehicleTypeId,
		currentDriverId = null,
		currentDriverName = null
	) {
		if (
			!isV2UI ||
			!vendorId ||
			!vehicleTypeId ||
			!(permissions?.search?.includes('driver') || permissions?.search?.includes('all'))
		) {
			setDriverData([]);
			return;
		}

		try {
			const res = await dispatch(
				searchDriversV2({
					filter_by: {
						vendor_id: vendorId,
						vehicle_type_ids: [Number(vehicleTypeId)],
						is_active: true,
					},
					offset: 0,
					limit: 1000,
				})
			);
			const data = (res?.rows || []).map((item) => ({
				value: item?.id,
				label: item?.driver_name || item?.name || '-',
				item,
			}));

			if (currentDriverId) {
				const driverIdNum = Number(currentDriverId);
				if (!data.find((d) => Number(d.value) === driverIdNum)) {
					data.unshift({
						value: driverIdNum,
						label: currentDriverName || '',
						item: { id: driverIdNum, driver_name: currentDriverName },
					});
				}
			}

			setDriverData(data);
		} catch (err) {
			setDriverData([]);
		}
	}

	async function handleRouteChange(routeId, setFieldValue) {
		setFieldValue('route_id', routeId);
		setFieldValue('vendor_type', undefined);
		setFieldValue('vendor_id', undefined);
		setFieldValue('vehicle_type', undefined);
		setFieldValue('vehicle_number', undefined);
		setFieldValue('driver_id', undefined);
		setFieldValue('driver_number', '');
		setVehicleData([]);
		setDriverData([]);

		const selectedRoute = routeDetailsById?.[routeId];
		if (!selectedRoute) return;

		const vendorType = selectedRoute?.vendor_details?.vendor_type || '';
		const vendorId = selectedRoute?.vendor_id || selectedRoute?.vendor_details?.id || '';
		const vehicleTypeName =
			selectedRoute?.vehicle_type_name || selectedRoute?.vehicle_type || '';
		const vehicleTypeId = selectedRoute?.vehicle_type_id || '';

		setFieldValue('vendor_type', vendorType || '');
		setFieldValue('vendor_id', vendorId || undefined);
		setFieldValue('vehicle_type', vehicleTypeName || '');

		if (vehicleTypeName && vehicleTypeId) {
			setVehicleTypeIdMap((prev) => ({
				...prev,
				[vehicleTypeName]: vehicleTypeId,
			}));
		}

		if (vendorId && vehicleTypeId) {
			await getVehiclesData(vendorId, vehicleTypeId, routeId);
		}
	}

	async function handleVehicleNumberChange(vehicleNumber, setFieldValue, values) {
		setFieldValue('vehicle_number', vehicleNumber);
		setFieldValue('driver_id', undefined);
		setFieldValue('driver_number', '');
		setDriverData([]);

		if (!isV2UI || !vehicleNumber) return;

		const vehicleTypeId = await resolveVehicleTypeId(values?.vehicle_type);
		const selectedVehicle = vehicleData.find((v) => v.value === vehicleNumber)?.item;
		const driverVendorId = selectedVehicle?.vendor_id || values?.vendor_id;
		if (!driverVendorId || !vehicleTypeId) return;

		await getDriverDataV2(driverVendorId, vehicleTypeId);
	}

	function handleDriverChange(driverId, setFieldValue) {
		setFieldValue('driver_id', driverId);
		const selectedDriver = (driverData || []).find(
			(driverOption) => Number(driverOption?.value) === Number(driverId)
		);
		setFieldValue('driver_number', selectedDriver?.item?.mobile_number || '');
	}

	function handleVendorTypeChange(type, setFieldValue) {
		setFieldValue('vendor_id', undefined);
		setFieldValue('driver_id', undefined);
		getVendorDataList(type);
	}

	async function handleVehicleTypeChange(vehicleType, setFieldValue, values) {
		setFieldValue('vehicle_number', undefined);
		setFieldValue('driver_id', undefined);
		setFieldValue('driver_number', '');
		if (isV2UI) {
			setVehicleData([]);
			setDriverData([]);
		}
		if (values.trip_type === 'regular') {
			getDriverData(vehicleType, values?.vendor_id);
		}
		if (isV2UI && values.trip_type === 'adhoc') {
			const vehicleTypeId = await resolveVehicleTypeId(vehicleType);
			if (values.vendor_id && vehicleTypeId) {
				await getVehiclesData(values.vendor_id, vehicleTypeId);
			}
		}
	}

	async function handleVendorChange(vendorId, setFieldValue, values) {
		setFieldValue('vehicle_number', undefined);
		setFieldValue('driver_id', undefined);
		setFieldValue('driver_number', '');
		if (isV2UI) {
			setVehicleData([]);
			setDriverData([]);
		}
		if (values.trip_type === 'regular') {
			getDriverData(values?.vehicle_type, vendorId);
		}
		if (isV2UI && values.trip_type === 'adhoc') {
			const vehicleTypeId = await resolveVehicleTypeId(values?.vehicle_type);
			if (vendorId && vehicleTypeId) {
				await getVehiclesData(vendorId, vehicleTypeId);
			}
		}
	}

	function handleTripTypeChange(tripType, setFieldValue, values) {
		setFieldValue('driver_id', undefined);
		if (tripType === 'regular') {
			getDriverData(values?.vehicle_type, values?.vendor_id);
		}
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	function handlePincodeChange(pincode, type, setFieldValue) {
		if (pincode && pincode.length === 6) {
			dispatch(getPincodeList(pincode)).then((res) => {
				const data = res.rows[0];
				const cityState = data
					? `${data.city || ''}${data.city && data.state ? ', ' : ''}${data.state || ''}`
					: '';
				if (type === 'source') {
					setSourceCity(cityState);
					setFieldValue('start_location_city_state', cityState);
				} else if (type === 'destination') {
					setDestCity(cityState);
					setFieldValue('end_location_city_state', cityState);
				}
			});
		} else if (type === 'source') {
			setSourceCity('');
			setFieldValue('start_location_city_state', '');
		} else if (type === 'destination') {
			setDestCity('');
			setFieldValue('end_location_city_state', '');
		}
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	async function handleOdometerUpload(file, setFieldValue) {
		// Check file size (5MB limit)
		if (file.size > 5 * 1024 * 1024) {
			message.error(FILE_SIZE_LIMIT_MSG);
			return false;
		}

		setUploadingOdometer(true);
		setUploadErrorOdometer('');
		try {
			const res = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: { entity: 'trip', keys: [file.name] },
				})
			);
			const fileData = res?.data?.[0];
			if (!fileData?.url || !fileData?.filename) throw new Error('Failed to get signed URL');
			const signedUrl = fileData.url;
			const s3Filename = fileData.filename;
			await fetch(signedUrl, {
				method: 'PUT',
				body: file,
				headers: { 'Content-Type': file.type },
			});
			// Store S3 key directly in odometer_start field
			setFieldValue('odometer_start', s3Filename ? [s3Filename] : []);
			console.log('s3Filename', s3Filename);
			message.success('File uploaded!');
		} catch (err) {
			setUploadErrorOdometer(UPLOAD_FAILED_MSG);
			message.error(UPLOAD_FAILED_MSG);
		} finally {
			setUploadingOdometer(false);
		}
		return false;
	}

	// Upload requirement proof for intracity
	async function handleRequirementProofUpload(file, setFieldValue) {
		if (file.size > 5 * 1024 * 1024) {
			message.error(FILE_SIZE_LIMIT_MSG);
			return false;
		}
		setUploadingProof(true);
		setUploadErrorProof('');
		try {
			const res = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: { entity: 'trip', keys: [file.name] },
				})
			);
			const fileData = res?.data?.[0];
			if (!fileData?.url || !fileData?.filename) throw new Error('Failed to get signed URL');
			const signedUrl = fileData.url;
			const s3Filename = fileData.filename;
			await fetch(signedUrl, {
				method: 'PUT',
				body: file,
				headers: { 'Content-Type': file.type },
			});
			setFieldValue('requirement_proof', s3Filename ? [s3Filename] : []);
			message.success(UPLOAD_SUCCESS_MSG);
		} catch (err) {
			setUploadErrorProof(UPLOAD_FAILED_MSG);
			message.error(UPLOAD_FAILED_MSG);
		} finally {
			setUploadingProof(false);
		}
		return false;
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	const handleSubmit = async (values, { setSubmitting }) => {
		console.log('Form values:', values);
		console.log('odometer_start:', values.odometer_start);

		// Use v2 API when new UI is enabled
		if (isV2UI) {
			try {
				// V2: For edit send times in UTC; for create send times as-is (local)
				const momentFn = isEdit ? moment.utc : moment;
				const reportingDateISO = values.reporting_date
					? momentFn(values.reporting_date).format('YYYY-MM-DDTHH:mm:ss')
					: null;
				const reportingTime = values.reporting_date
					? momentFn(values.reporting_date).format('HH:mm:ss')
					: null;
				const startTimeISO = values.start_date
					? momentFn(values.start_date).format('YYYY-MM-DDTHH:mm:ss')
					: null;

				const vehicleTypeId = await resolveVehicleTypeId(values.vehicle_type);
				if (!vehicleTypeId) {
					message.error('Vehicle type id not found for selected vehicle type');
					setSubmitting(false);
					return;
				}

				// Ensure odometer_start is always an array and filter out empty values
				const odometerStartFiles = Array.isArray(values.odometer_start)
					? values.odometer_start.filter((x) => x && x.length >= 5)
					: [];

				// Build v2 payload structure
				const v2Payload = {
					project_id: values.project_id,
					vendor_id: values.vendor_id,
					driver_id: values.driver_id,
					route_id: values.route_id ? values.route_id : null,
					vehicle_number: values.vehicle_number,
					vehicle_type_id: Number(vehicleTypeId),
					start_date: startTimeISO,
					start_time: startTimeISO,
					id: isEdit ? id : null,
					end_time: null, // For new trips, end_time is null
					reporting_date: reportingDateISO,
					reporting_time: reportingTime,
					start_km: Number(values.start_km) || 0,
					remarks: values.remarks,
					trip_info: {
						odometer_reading_start: Number(values.start_km) || 0,
						odometer_reading_end: null, // For new trips, end reading is null
						movement_type: 'Intracity',
						client_rate: Number(values.client_rate) || 0,
						vendor_rate: Number(values.vendor_rate) || 0,
						requirement_proof:
							Array.isArray(values.requirement_proof) &&
							values.requirement_proof.length > 0
								? values.requirement_proof
								: [],
					},
					trip_documents: {
						odometer_start: odometerStartFiles,
						odometer_end: [],
					},
					trip_end_reason: null, // For new trips, end reason is null
					...(values.trip_type === 'adhoc'
						? {
								route_type: values.route_type,
								address: {
									source: values.start_address,
									destination: values.destination_address,
									intermediary: Array.isArray(values.intermediary_addresses)
										? values.intermediary_addresses.filter(Boolean)
										: [],
								},
						  }
						: {}),
				};

				// Determine request type based on trip_type and mode
				const createType = values.trip_type === 'regular' ? 'regular_trip' : 'adhoc_trip';

				// Editing a pending margin request: update the stash + the approver-facing
				// columns on the request. Nothing is created — there is still no trip.
				if (isMarginRequestEdit) {
					const updateRes = await dispatch(
						addUser({
							data: {
								update_type: 'intracity_margin_request',
								data: { ...v2Payload, adhoc_request_id: marginRequestId },
							},
							setErrors: () => {},
							isEditMode: true,
							version: 'v2',
						})
					);
					setSubmitting(false);
					if (updateRes?.status === 'success') {
						message.success(
							updateRes?.data?.message ||
								'Request updated. It remains with the City Manager for approval.'
						);
						navigate(`/trips/adhoc-request/${marginRequestId}`);
					}
					return;
				}

				// Call v2 endpoint
				const createRes = await dispatch(
					addUser({
						data: {
							...(isEdit ? { update_type: 'trip' } : { create_type: createType }),
							data: v2Payload,
						},
						setErrors: () => {},
						isEditMode: isEdit,
						version: 'v2',
					})
				);
				if (isEdit) {
					const porApplies = ['pending', 'rejected'].includes(
						formData?.por_approval_status
					);
					const currentPorKey =
						Array.isArray(values.requirement_proof) &&
						values.requirement_proof.length > 0
							? values.requirement_proof[0]
							: null;
					if (
						porApplies &&
						currentPorKey &&
						currentPorKey !== originalPorKeyRef.current
					) {
						await dispatch(
							reuploadPoR({ trip_id: Number(id) || id, document_key: currentPorKey })
						);
					}
				}

				setSubmitting(false);
				if (isEdit) {
					message.success('Trip edited successfully');
					navigate(INTRACITY_TRIPS_ROUTE);
				} else if (createRes?.data?.margin_approval_required) {
					// Below the 5% margin threshold: the trip exists but is held back until
					// the City Manager and then the VP approve it. Say so plainly and land the
					// user on the tab where it is waiting, rather than the trip list it is
					// deliberately absent from.
					message.warning(
						createRes?.data?.message ||
							'Margin is below 5%. Sent for City Manager approval; the trip will go live once approved.',
						6
					);
					navigate(INTRACITY_TRIPS_ROUTE, {
						state: { refresh: true, activeTab: 'pending' },
					});
				} else {
					message.success('Trip created successfully');
					navigate(INTRACITY_TRIPS_ROUTE, { state: { refresh: true } });
				}
			} catch (error) {
				console.error('Error creating trip (v2):', error);
				message.error('Failed to create trip');
				setSubmitting(false);
			}
			return;
		}

		// V1 API logic (existing code)
		let payload;
		if (values.trip_type === 'regular') {
			const { reporting_date_payload, reporting_time_payload } = getServerFormatedDate(
				values.reporting_date,
				'reporting_date'
			);
			const { start_date_payload, start_time_payload } = getServerFormatedDate(
				values.start_date,
				'start_date'
			);

			payload = {
				trip_type: 'regular',
				city: values.city,
				project_id: values.project_id,
				vendor_id: values.vendor_id,
				driver_id: values.driver_id,
				vehicle_number: values.vehicle_number,
				vehicle_type: values.vehicle_type,
				reporting_date: reporting_date_payload,
				reporting_time: `${reporting_date_payload} ${reporting_time_payload}`,
				start_date: start_date_payload,
				start_time: `${start_date_payload} ${start_time_payload}`,
				start_km: values.start_km,
				trip_documents: {
					odometer_start: Array.isArray(values.odometer_start)
						? values.odometer_start.filter((x) => x && x.length >= 5)
						: [],
				},
				info: {
					is_inter_city: false,
				},
				...(isEdit ? { id } : {}),
			};
		} else {
			const trip_info = {
				client_rate: Number(values.client_rate),
				vendor_rate: Number(values.vendor_rate),
				is_inter_city: false,
				driver_name: values.driver_name || '',
				driver_number: values.driver_number || '',
				requirement_proof:
					Array.isArray(values.requirement_proof) && values.requirement_proof.length > 0
						? values.requirement_proof
						: [''],
				...(values.cross_vendor_remark
					? { cross_vendor_vehicle_remark: values.cross_vendor_remark }
					: {}),
			};

			const { reporting_date_payload, reporting_time_payload } = getServerFormatedDate(
				values.reporting_date,
				'reporting_date'
			);
			const { start_date_payload, start_time_payload } = getServerFormatedDate(
				values.start_date,
				'start_date'
			);

			// Ensure odometer_start is always an array and filter out empty values
			const odometerStartFiles = Array.isArray(values.odometer_start)
				? values.odometer_start.filter((x) => x && x.length >= 5)
				: [];

			payload = {
				trip_type: values.trip_type,
				city: values.city,
				project_id: values.project_id,
				vendor_id: values.vendor_id,
				vehicle_type: values.vehicle_type,
				vehicle_number: values.vehicle_number,
				reporting_date: reporting_date_payload,
				reporting_time: `${reporting_date_payload} ${reporting_time_payload}`,
				start_date: start_date_payload,
				start_time: `${start_date_payload} ${start_time_payload}`,
				destination_pincode: values.end_location_pincode,
				source_pincode: values.start_location_pincode,
				start_km: values.start_km,
				remarks: values.remarks,
				trip_documents: {
					odometer_start: odometerStartFiles,
				},
				info: trip_info,
				...(isEdit ? { id } : {}),
			};
		}

		dispatch(
			addUser({
				data: {
					data: payload,
					...(isEdit ? { update_type: 'trip' } : { create_type: 'trip' }),
				},
				setErrors: () => {},
				isEditMode: isEdit,
			})
		)
			.then(() => {
				setSubmitting(false);
				if (isEdit) {
					message.success('Trip edited successfully');
					navigate(INTRACITY_TRIPS_ROUTE);
				} else {
					// Show success message for new trip creation
					message.success('Trip created successfully');
					navigate(INTRACITY_TRIPS_ROUTE, { state: { refresh: true } });
				}
			})
			.catch(() => {
				setSubmitting(false);
			});
	};

	// The stash holds the exact create payload; map it back onto the form's field names.
	useEffect(() => {
		if (!isMarginRequestEdit) return;
		const p = marginRequestPayload || {};
		const ti = p.trip_info || {};
		setFormData({
			trip_type: 'adhoc',
			project_id: p.project_id,
			route_id: p.route_id,
			route_type: p.route_type,
			vendor_id: p.vendor_id,
			vehicle_number: p.vehicle_number,
			vehicle_type: p.vehicle_type,
			driver_id: p.driver_id,
			reporting_date: p.reporting_date,
			start_date: p.start_date,
			start_km: p.start_km,
			remarks: p.remarks,
			client_rate: ti.client_rate,
			vendor_rate: ti.vendor_rate,
			requirement_proof: ti.requirement_proof || [],
			start_address: p.address?.source || '',
			destination_address: p.address?.destination || '',
			intermediary_addresses: p.address?.intermediary || [],
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMarginRequestEdit]);

	if (isEdit && (loading || !formData?.id)) {
		return <div>Loading...</div>;
	}

	return (
		<IntracityTripForm
			isEditMode={isEdit}
			formData={formData}
			cities={isV2UI ? cityOptionsV2 : cities}
			trip_reason={[]}
			projectData={projectData}
			vendorsData={vendorsData}
			vehicleTypes={VehicleTypes}
			vehicleTypeIdMap={vehicleTypeIdMap}
			vehicleData={vehicleData}
			driverData={driverData}
			routeData={routeData}
			isV2UI={isV2UI}
			projectAdhocFlag={ProjectAdhocFlag}
			sourceCity={sourceCity}
			destCity={destCity}
			uploadingOdometer={uploadingOdometer}
			uploadErrorOdometer={uploadErrorOdometer}
			uploadingProof={uploadingProof}
			uploadErrorProof={uploadErrorProof}
			vendorUserType={vendorUserType}
			formRef={formRef}
			onCityChange={handleCitiesChange}
			onProjectChange={handleProjectChange}
			onVendorTypeChange={handleVendorTypeChange}
			onVehicleTypeChange={handleVehicleTypeChange}
			onVendorChange={handleVendorChange}
			onTripTypeChange={handleTripTypeChange}
			onRouteChange={handleRouteChange}
			onVehicleNumberChange={handleVehicleNumberChange}
			clientAllowsCrossVendor={crossVendorAllowed}
			onCrossVendorSelect={handleCrossVendorSelect}
			onDriverChange={handleDriverChange}
			onPincodeChange={handlePincodeChange}
			onProofUpload={handleRequirementProofUpload}
			onOdometerUpload={handleOdometerUpload}
			onSubmit={handleSubmit}
			navigate={navigate}
			source_entry={formData?.source_entry || ''}
		/>
	);
}
